// Tenantra Inc. - Admin Dashboard (Microsoft 365 Style - Compact)
import { useEffect, useState } from 'react';
import { 
  Shield, Users, Home, FileText, AlertTriangle, Settings,
  CheckCircle, Clock, LogOut, LayoutDashboard, Search,
  ScrollText, Bell, Menu, X, ChevronRight, Building2, 
  Trash2, ShieldOff, UserCog, Edit, Eye, EyeOff, RotateCcw, Save, Loader2,
  CreditCard, Percent, Headphones, MessageSquare, ArrowUp, ArrowDown, ArrowUpDown,
  User, BarChart3, DollarSign, Tag, ChevronDown, Mail, Plus, Filter, Key,
  TrendingUp, TrendingDown, Wallet, Activity, PieChart, Calendar, FolderOpen, Download, Send, Briefcase, ShieldCheck, Rocket, Banknote
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCurrentUser, logout } from '@/services/auth';
import api from '@/services/api';
import { toast } from 'sonner';
import MFASettings from '@/components/MFASettings';
import { LogoMark } from '@/components/Logo';
import { AdminCommandPalette } from '@/components/dashboard/AdminCommandPalette';
import { CommandTrigger } from '@/components/dashboard/CommandPalette';
import { TableSkeleton, exportCsv, ExportButton } from '@/components/dashboard/TableKit';
import { formatDate } from '@/utils/date';
import { CareersPanel } from '@/components/admin/CareersPanel';
import { EquifaxReportingPanel } from '@/components/admin/EquifaxReportingPanel';
import { PaymentFeesPanel } from '@/components/admin/PaymentFeesPanel';
import { FeeRevenuePanel } from '@/components/admin/FeeRevenuePanel';
import { FeePayoutsPanel } from '@/components/admin/FeePayoutsPanel';
import { WalletsPanel } from '@/components/admin/WalletsPanel';
import AdminStaffPanel from '@/components/admin/AdminStaffPanel';
import WaitlistPanel from '@/components/admin/WaitlistPanel';
import ContactsPanel from '@/components/admin/ContactsPanel';
import { PropertyReviewPanel } from '@/components/admin/PropertyReviewPanel';
import { PromoCodesPanel } from '@/components/admin/PromoCodesPanel';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart as RechartPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';

// Admin Sidebar - Compact Microsoft 365 Style
function AdminSidebar({ isOpen, onClose, counts }) {
  const currentPath = window.location.pathname;
  const user = getCurrentUser();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [access, setAccess] = useState(null);
  useEffect(() => { api.get('/admin/staff/me/access').then(r => setAccess(r.data)).catch(() => setAccess({ is_owner: true, sections: [] })); }, []);

  const HREF_SECTION = {
    'analytics': 'analytics', 'landlords': 'landlords', 'properties': 'properties', 'ownership-review': 'ownership_review',
    'tenants': 'tenants', 'reports': 'reports', 'tenancies': 'tenancies', 'consent-requests': 'consent_requests',
    'subscriptions': 'subscriptions', 'discounts': 'discounts', 'promo-codes': 'discounts', 'payments': 'payments',
    'support-tickets': 'tickets', 'disputes': 'disputes', 'careers': 'careers', 'equifax-reporting': 'equifax',
    'payment-fees': 'payment_fees', 'fee-revenue': 'fee_revenue', 'wallets': 'wallets', 'users': 'users',
  };
  const slug = (href) => href.split('/').pop();
  const canSee = (href) => {
    if (slug(href) === 'dashboard') return true;
    if (!access || access.is_owner) return true;
    const sec = HREF_SECTION[slug(href)];
    return sec ? (access.sections || []).includes(sec) : false; // owner-only tools hidden for staff
  };
  
  const navSections = [
    {
      label: 'Main',
      items: [
        { href: '/portal-8k2m9x/dashboard', label: 'Home', icon: LayoutDashboard },
        { href: '/portal-8k2m9x/analytics', label: 'Analytics', icon: BarChart3 },
        { href: '/portal-8k2m9x/landlords', label: 'Landlords', icon: Users, count: counts?.pendingLandlords, alert: counts?.pendingLandlords > 0 },
        { href: '/portal-8k2m9x/properties', label: 'Properties', icon: Home },
        { href: '/portal-8k2m9x/ownership-review', label: 'Ownership Review', icon: ShieldCheck, count: counts?.propertiesInReview, alert: counts?.propertiesInReview > 0 },
        { href: '/portal-8k2m9x/tenants', label: 'Tenants', icon: User },
        { href: '/portal-8k2m9x/reports', label: 'Reports', icon: FileText },
        { href: '/portal-8k2m9x/tenancies', label: 'Tenancies', icon: Building2 },
        { href: '/portal-8k2m9x/consent-requests', label: 'Consent Requests', icon: CheckCircle },
      ]
    },
    {
      label: 'Billing',
      items: [
        { href: '/portal-8k2m9x/subscriptions', label: 'Subscriptions', icon: CreditCard },
        { href: '/portal-8k2m9x/discounts', label: 'Discounts', icon: Percent },
        { href: '/portal-8k2m9x/promo-codes', label: 'Promo Codes', icon: Tag },
        { href: '/portal-8k2m9x/payments', label: 'Payments', icon: DollarSign },
      ]
    },
    {
      label: 'Reporting',
      items: [
        { href: '/portal-8k2m9x/equifax-reporting', label: 'Equifax Reporting', icon: Send },
        { href: '/portal-8k2m9x/payment-fees', label: 'Payment Fees', icon: DollarSign },
        { href: '/portal-8k2m9x/fee-revenue', label: 'Fee Revenue', icon: TrendingUp },
        { href: '/portal-8k2m9x/fee-payouts', label: 'Fee Payouts', icon: Banknote },
        { href: '/portal-8k2m9x/wallets', label: 'Rent Wallets', icon: Wallet },
      ]
    },
    {
      label: 'Support',
      items: [
        { href: '/portal-8k2m9x/support-tickets', label: 'Tickets', icon: Headphones, count: counts?.openTickets, alert: counts?.openTickets > 0 },
        { href: '/portal-8k2m9x/disputes', label: 'Disputes', icon: AlertTriangle, count: counts?.openDisputes, alert: counts?.openDisputes > 0 },
        { href: '/portal-8k2m9x/careers', label: 'Careers', icon: Briefcase, count: counts?.newApplications, alert: counts?.newApplications > 0 },
        { href: '/portal-8k2m9x/contacts', label: 'Contact Us', icon: Mail, count: counts?.newContacts, alert: counts?.newContacts > 0 },
      ]
    },
    {
      label: 'System',
      items: [
        { href: '/portal-8k2m9x/users', label: 'Users', icon: UserCog },
        { href: '/portal-8k2m9x/admin-team', label: 'Admin Team', icon: UserCog },
        { href: '/portal-8k2m9x/waitlist', label: 'Waitlist', icon: Rocket },
        { href: '/portal-8k2m9x/teams', label: 'Teams', icon: Users },
        { href: '/portal-8k2m9x/api-keys', label: 'API Keys', icon: Key },
        { href: '/portal-8k2m9x/email-templates', label: 'Emails', icon: Mail },
        { href: '/portal-8k2m9x/newsletter', label: 'Newsletter', icon: Send },
        { href: '/portal-8k2m9x/client-documents', label: 'Client Docs', icon: FolderOpen },
        { href: '/portal-8k2m9x/audit-logs', label: 'Audit', icon: ScrollText },
        { href: '/portal-8k2m9x/settings', label: 'Settings', icon: Settings },
      ]
    }
  ];

  const isItemActive = (href) => {
    if (href === '/portal-8k2m9x/dashboard') return currentPath === href || currentPath === '/portal-8k2m9x';
    return currentPath.startsWith(href);
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[3px] z-40 lg:hidden animate-in fade-in duration-200" onClick={onClose} />}
      <div className={`fixed top-0 bottom-0 left-0 bg-[#fafafa] flex flex-col z-50 w-[220px] transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 border-r border-slate-200`}>
        {/* Header */}
        <div className="h-12 px-3 flex items-center justify-between border-b border-slate-200">
          <a href="/portal-8k2m9x/dashboard" className="flex items-center gap-2" data-testid="admin-sidebar-logo">
            <LogoMark className="w-6 h-6" color="#6D28D9" />
            <div className="leading-none">
              <span className="font-bold tracking-tight text-slate-900 text-sm" style={{ fontFamily: "'Chivo', sans-serif" }}>Tenantra</span>
              <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-violet-700 mt-0.5">Admin</span>
            </div>
          </a>
          <button onClick={onClose} className="lg:hidden p-1 hover:bg-slate-200 rounded text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {navSections.map((section, idx) => {
            const visibleItems = section.items.filter((item) => canSee(item.href));
            if (visibleItems.length === 0) return null;
            return (
            <div key={section.label} className={idx > 0 ? 'mt-4' : ''}>
              <div className="px-2 py-1">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{section.label}</span>
              </div>
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isItemActive(item.href);
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={onClose}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-[12px] transition-all relative ${isActive ? 'bg-white text-violet-700 shadow-sm font-medium' : 'text-slate-600 hover:bg-white/60 hover:text-slate-900'}`}
                    >
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 bg-violet-600 rounded-r" />}
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-violet-600' : item.alert ? 'text-red-500' : 'text-slate-400'}`} />
                      <span className="flex-1">{item.label}</span>
                      {item.count > 0 && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${item.alert ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                          {item.count}
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
            );
          })}
        </nav>
        
        {/* User Footer */}
        <div className="p-2 border-t border-slate-200 bg-white/50">
          <div className="relative">
            <button onClick={() => setShowUserMenu(!showUserMenu)} className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer transition-colors">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center text-white font-medium text-[10px]">A</div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[11px] font-medium text-slate-800 truncate">Admin</div>
                <div className="text-[9px] text-slate-400 truncate">{user?.email}</div>
              </div>
              <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${showUserMenu ? 'rotate-90' : ''}`} />
            </button>
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50">
                  <button onClick={() => { setShowUserMenu(false); logout(); }} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-slate-600 hover:bg-slate-50">
                    <LogOut className="w-3 h-3 text-slate-400" />
                    <span>Sign out</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Status Badge - Compact
const BASE_MONTHLY = { starter: 29, professional: 59, enterprise: 99 };
const INTERVAL_MONTHS = { monthly: 1, '6_month': 6, annual: 12 };
const INTERVAL_KEY = { monthly: 'monthly_discount', '6_month': 'six_month_discount', annual: 'annual_discount' };

function DiscountPreview({ settings }) {
  const baseOf = (plan) => Number(settings[`base_${plan}`]) || BASE_MONTHLY[plan];
  const pct = (plan, iv) => Math.min((settings[INTERVAL_KEY[iv]] || 0) + (settings[`${plan}_discount`] || 0) + (settings.global_discount || 0), 90);
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="discount-preview">
      <h3 className="text-xs font-semibold text-slate-900 mb-2">Resulting prices (what landlords see)</h3>
      {settings.trial_enabled && (
        <p className="text-[10px] text-emerald-700 mb-2" data-testid="preview-trial-note">First-time subscribers start with a {settings.trial_days}-day free trial, then auto-convert to the price below.</p>
      )}
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-slate-500">
          <tr><th className="text-left py-1 font-medium">Plan</th>{Object.keys(INTERVAL_MONTHS).map(iv => <th key={iv} className="text-right py-1 font-medium">{iv === '6_month' ? '6-Month' : iv.charAt(0).toUpperCase() + iv.slice(1)}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {Object.keys(BASE_MONTHLY).map(plan => (
            <tr key={plan}>
              <td className="py-1.5 capitalize text-slate-700">{plan}</td>
              {Object.keys(INTERVAL_MONTHS).map(iv => {
                const months = INTERVAL_MONTHS[iv]; const list = baseOf(plan) * months; const p = pct(plan, iv); const total = list * (100 - p) / 100;
                return (
                  <td key={iv} className="py-1.5 text-right" data-testid={`preview-${plan}-${iv}`}>
                    <span className="font-medium text-slate-900">${(total / months).toFixed(2)}/mo</span>
                    <span className="text-slate-400"> · ${total.toFixed(2)}</span>
                    {p > 0 && <span className="text-emerald-600"> −{p}%</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    pending_verification: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    verified: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    rejected: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    open: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    resolved: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
    closed: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
    under_review: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    waiting_on_customer: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
    landlord_responded: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
    disputed: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    canceled: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    none: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
  };
  const c = config[status] || config.pending;
  const label = status === 'pending_verification' ? 'Pending' : status?.replace(/_/g, ' ') || 'None';
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </span>
  );
}

// Plan Badge - Compact
function PlanBadge({ plan }) {
  const config = {
    starter: { bg: 'bg-blue-50', text: 'text-blue-700' },
    professional: { bg: 'bg-purple-50', text: 'text-purple-700' },
    enterprise: { bg: 'bg-teal-50', text: 'text-teal-700' },
    none: { bg: 'bg-slate-100', text: 'text-slate-500' },
  };
  const c = config[plan] || config.none;
  const label = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'No Plan';
  
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${c.bg} ${c.text}`}>{label}</span>;
}


function AdminSettings({ user, onRefresh }) {
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChangeEmail = async () => {
    if (!newEmail || !password) { toast.error('Email and password are required'); return; }
    if (newEmail === user?.email) { toast.error('New email is the same as current'); return; }
    setSaving(true);
    try {
      const res = await api.put('/auth/change-email', { new_email: newEmail, password });
      toast.success('Email updated. Please log in again.');
      setPassword('');
      // Update localStorage and reload
      const stored = JSON.parse(localStorage.getItem('tenantra_user') || '{}');
      stored.email = res.data.email;
      localStorage.setItem('tenantra_user', JSON.stringify(stored));
      setTimeout(() => { window.location.reload(); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-sm font-semibold text-slate-800">Settings</h1>
        <p className="text-slate-500 text-[11px]">Manage your admin account</p>
      </div>

      <div className="max-w-md space-y-4">
        {/* Current info */}
        <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Account Info</h3>
          <div className="space-y-2">
            <div className="flex justify-between py-1.5 border-b border-slate-100">
              <span className="text-xs text-slate-500">Current Email</span>
              <span className="text-xs font-medium text-slate-800">{user?.email}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100">
              <span className="text-xs text-slate-500">Role</span>
              <span className="text-xs font-medium text-slate-800">Administrator</span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-xs text-slate-500">Status</span>
              <span className="text-xs font-medium text-emerald-600">Active</span>
            </div>
          </div>
        </div>

        {/* Change email */}
        <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Change Email</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">New Email</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="new@email.com"
                className="h-8 text-xs bg-white"
                data-testid="settings-new-email"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Confirm Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="h-8 text-xs bg-white pr-8"
                  data-testid="settings-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <Button
              onClick={handleChangeEmail}
              disabled={saving || !newEmail || !password}
              className="h-8 px-4 text-xs bg-slate-800 hover:bg-slate-900 text-white border-0"
              data-testid="settings-save-email"
            >
              {saving ? <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span> : 'Update Email'}
            </Button>
          </div>
        </div>

        {/* MFA */}
        <MFASettings />
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [listSort, setListSort] = useState({ key: 'name', dir: 'asc' });
  const [actionLoading, setActionLoading] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [modalData, setModalData] = useState({});
  const [expandedDispute, setExpandedDispute] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveDecision, setResolveDecision] = useState('keep_entry');
  
  // Discount + pricing + trial settings
  const [discountSettings, setDiscountSettings] = useState({
    starter_discount: 0, professional_discount: 0, enterprise_discount: 0,
    monthly_discount: 0, six_month_discount: 20, annual_discount: 30, global_discount: 0,
    base_starter: 29, base_professional: 59, base_enterprise: 99,
    trial_enabled: false, trial_days: 14
  });
  
  // Ticket management
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketComment, setTicketComment] = useState('');
  const [ticketStatus, setTicketStatus] = useState('');
  
  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({});
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Billing stats
  const [billingStats, setBillingStats] = useState(null);
  const [feeOwed, setFeeOwed] = useState(null);
  
  // Consent requests & API keys
  const [consentRequests, setConsentRequests] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  
  // Edit modals for Tenant and Property
  const [editingTenant, setEditingTenant] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [tenantForm, setTenantForm] = useState({});
  const [propertyForm, setPropertyForm] = useState({});
  
  // Analytics data
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Newsletter
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [newsletterForm, setNewsletterForm] = useState({ subject: '', body_html: '', email_type: 'product_update' });
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  
  const currentPath = window.location.pathname;
  const user = getCurrentUser();

  useEffect(() => { fetchData(); setSidebarOpen(false); setSearchTerm(''); }, [currentPath]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statsRes = await api.get('/admin/dashboard/stats');
      setStats(statsRes.data);

      if (currentPath.includes('analytics')) {
        const res = await api.get('/admin/analytics');
        setAnalyticsData(res.data);
      } else if ((currentPath.includes('dashboard') || currentPath === '/portal-8k2m9x' || currentPath === '/portal-8k2m9x/')) {
        // Overview home: pull revenue + fees-owed snapshots (both optional / permission-gated).
        api.get('/admin/billing/stats').then(r => setBillingStats(r.data)).catch(() => {});
        const m = new Date().toISOString().slice(0, 7);
        api.get('/admin/fee-payouts/summary', { params: { month: m } })
          .then(r => setFeeOwed({ owed: r.data.totals.owed, landlords: r.data.totals.landlords_owing }))
          .catch(() => setFeeOwed(null));
        api.get('/admin/analytics').then(r => setAnalyticsData(r.data)).catch(() => {});
      } else if (currentPath.includes('landlords') && !currentPath.includes('dashboard')) {
        const res = await api.get('/admin/verifications/landlords?status=all&limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('properties') && !currentPath.includes('dashboard')) {
        const res = await api.get('/admin/properties/all?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('tenants') && !currentPath.includes('dashboard')) {
        const res = await api.get('/admin/tenants/all?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('reports') && !currentPath.includes('dashboard')) {
        const res = await api.get('/admin/reports/all?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('tenancies') && !currentPath.includes('dashboard')) {
        const res = await api.get('/admin/tenancies/all?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('disputes')) {
        const res = await api.get('/admin/disputes/all?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('audit')) {
        const res = await api.get('/admin/audit-logs?limit=50');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('users') && !currentPath.includes('subscriptions')) {
        const res = await api.get('/admin/users?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('email-templates')) {
        const res = await api.get('/email-templates');
        setEmailTemplates(res.data || []);
      } else if (currentPath.includes('subscriptions')) {
        const res = await api.get('/admin/subscriptions?limit=100&include_no_plan=true');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('support-tickets')) {
        const res = await api.get('/admin/support-tickets?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('discounts')) {
        try {
          const res = await api.get('/admin/discounts');
          setDiscountSettings({ ...discountSettings, ...res.data });
        } catch (e) { /* use defaults */ }
      } else if (currentPath.includes('payments')) {
        const [paymentsRes, billingRes] = await Promise.all([
          api.get('/admin/payments/all?limit=100'),
          api.get('/admin/billing/stats')
        ]);
        setData({ items: paymentsRes.data.items || [] });
        setBillingStats(billingRes.data);
      } else if (currentPath.includes('teams')) {
        const res = await api.get('/admin/teams/all?limit=100');
        setData({ items: res.data.items || [] });
      } else if (currentPath.includes('newsletter')) {
        const [subsRes, campRes] = await Promise.all([
          api.get('/admin/newsletter-subscribers'),
          api.get('/admin/newsletter/campaigns')
        ]);
        setSubscribers(subsRes.data.subscribers || []);
        setCampaigns(campRes.data.campaigns || []);
      }
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  // Action handlers
  const handleAction = async (action, id, payload = {}) => {
    setActionLoading(id);
    try {
      if (action === 'approve_landlord') await api.post(`/admin/verifications/landlords/${id}/approve`);
      else if (action === 'reject_landlord') await api.post(`/admin/verifications/landlords/${id}/reject`, { reason: 'Documents insufficient' });
      else if (action === 'approve_property') await api.post(`/admin/verifications/properties/${id}/approve`);
      else if (action === 'reject_property') await api.post(`/admin/verifications/properties/${id}/reject`, { reason: 'Documents insufficient' });
      else if (action === 'delete_property') await api.delete(`/admin/properties/${id}`);
      else if (action === 'delete_tenant') await api.delete(`/admin/tenants/${id}`);
      else if (action === 'delete_report') await api.delete(`/admin/reports/${id}`);
      else if (action === 'delete_tenancy') await api.delete(`/admin/tenancies/${id}`);
      else if (action === 'withdraw_consent') await api.post(`/esign/tenancies/${id}/withdraw`, { reason: 'Written withdrawal received from tenant' });
      else if (action === 'delete_user') await api.delete(`/admin/users/${id}`);
      else if (action === 'delete_team') await api.delete(`/admin/teams/${id}`);
      else if (action === 'disable_mfa') await api.post(`/admin/users/${id}/disable-mfa`);
      else if (action === 'resolve_dispute') await api.post(`/admin/disputes/${id}/resolve`, payload);
      else if (action === 'assign_plan') await api.post(`/admin/subscriptions/${id}/upgrade`, payload);
      else if (action === 'adjust_payment') await api.post(`/admin/payments/${id}/adjust`, payload);
      toast.success('Action completed');
      setModal(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActionLoading(null); }
  };

  const handleSaveDiscounts = async () => {
    setActionLoading('discounts');
    try {
      await api.post('/admin/discounts', discountSettings);
      toast.success('Discounts saved');
    } catch (e) { toast.error('Failed to save'); }
    finally { setActionLoading(null); }
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;
    setActionLoading(selectedTicket.id);
    try {
      await api.put(`/admin/support-tickets/${selectedTicket.id}/status`, { status: ticketStatus, comment: ticketComment || undefined });
      toast.success('Ticket updated');
      setSelectedTicket(null);
      setTicketStatus('');
      setTicketComment('');
      fetchData();
    } catch (e) { toast.error('Failed to update'); }
    finally { setActionLoading(null); }
  };

  // Handle tenant update
  const handleUpdateTenant = async () => {
    if (!editingTenant) return;
    setActionLoading(editingTenant.id);
    try {
      await api.put(`/admin/tenants/${editingTenant.id}`, tenantForm);
      toast.success('Tenant updated');
      setEditingTenant(null);
      setTenantForm({});
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update tenant'); }
    finally { setActionLoading(null); }
  };

  // Handle property update
  const handleUpdateProperty = async () => {
    if (!editingProperty) return;
    setActionLoading(editingProperty.id);
    try {
      await api.put(`/admin/properties/${editingProperty.id}`, propertyForm);
      toast.success('Property updated');
      setEditingProperty(null);
      setPropertyForm({});
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update property'); }
    finally { setActionLoading(null); }
  };

  const counts = {
    pendingLandlords: stats?.landlords?.pending_verification || 0,
    pendingProperties: stats?.properties?.pending_verification || 0,
    propertiesInReview: stats?.properties?.in_review || 0,
    openDisputes: stats?.disputes?.pending_action || 0,
    openTickets: stats?.support_tickets?.open || 0,
    newApplications: stats?.careers?.new_applications || 0,
  };

  const getPageTitle = () => {
    if (currentPath === '/portal-8k2m9x/dashboard' || currentPath === '/portal-8k2m9x') return 'Overview';
    const mapping = {
      'landlords': 'Landlords', 'properties': 'Properties', 'ownership-review': 'Ownership Review', 'tenants': 'Tenants',
      'reports': 'Reports', 'tenancies': 'Tenancies', 'subscriptions': 'Subscriptions',
      'discounts': 'Discounts', 'promo-codes': 'Promo Codes', 'payments': 'Payments', 'support-tickets': 'Support Tickets', 'careers': 'Careers', 'equifax-reporting': 'Equifax Reporting', 'payment-fees': 'Payment Fees', 'fee-revenue': 'Fee Revenue', 'fee-payouts': 'Fee Payouts', 'wallets': 'Rent Wallets',
      'disputes': 'Disputes', 'users': 'Users', 'teams': 'Teams', 'analytics': 'Analytics',
      'email-templates': 'Email Templates', 'client-documents': 'Client Documents', 'audit': 'Audit Log', 'settings': 'Settings'
    };
    for (const [key, val] of Object.entries(mapping)) {
      if (currentPath.includes(key)) return val;
    }
    return 'Admin';
  };

  // Render Dashboard Overview — command-center home
  const renderDashboard = () => {
    const hr = new Date().getHours();
    const greeting = hr < 12 ? 'Good morning' : hr < 18 ? 'Good afternoon' : 'Good evening';
    const dateStr = new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
    const rawName = (user?.email || 'Admin').split('@')[0].split(/[._]/)[0];
    const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);
    const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const actions = [
      { id: 'verify', label: 'Verifications', count: counts.pendingLandlords + counts.pendingProperties, href: '/portal-8k2m9x/ownership-review', icon: ShieldCheck, hint: `${counts.pendingLandlords} landlords · ${counts.pendingProperties} properties`, tone: 'amber' },
      { id: 'disputes', label: 'Open disputes', count: counts.openDisputes, href: '/portal-8k2m9x/disputes', icon: AlertTriangle, hint: 'Awaiting a decision', tone: 'red' },
      { id: 'tickets', label: 'Support tickets', count: counts.openTickets, href: '/portal-8k2m9x/support-tickets', icon: Headphones, hint: 'Open & unresolved', tone: 'blue' },
      { id: 'careers', label: 'New applications', count: counts.newApplications, href: '/portal-8k2m9x/careers', icon: Briefcase, hint: 'Candidates to review', tone: 'violet' },
    ];
    if (feeOwed && feeOwed.landlords > 0) {
      actions.push({ id: 'fees', label: 'Fees to sweep', count: feeOwed.landlords, href: '/portal-8k2m9x/fee-payouts', icon: Banknote, hint: `${money(feeOwed.owed)} across ${feeOwed.landlords} landlord${feeOwed.landlords === 1 ? '' : 's'}`, tone: 'teal' });
    }
    const totalAttention = actions.reduce((a, x) => a + (x.count || 0), 0);
    const toneCls = {
      amber: 'text-amber-600 bg-amber-50 ring-amber-100',
      red: 'text-red-600 bg-red-50 ring-red-100',
      blue: 'text-blue-600 bg-blue-50 ring-blue-100',
      violet: 'text-violet-600 bg-violet-50 ring-violet-100',
      teal: 'text-teal-600 bg-teal-50 ring-teal-100',
    };

    const revenue = [
      { label: 'Monthly recurring', value: money(billingStats?.mrr || 0), icon: TrendingUp },
      { label: 'Active subscriptions', value: billingStats?.active_subscriptions?.total ?? 0, icon: CreditCard },
      { label: 'Total revenue', value: money(billingStats?.total_revenue || 0), icon: DollarSign },
      { label: 'Payments (30d)', value: billingStats?.recent_payments_30d ?? 0, icon: Activity },
      { label: 'Fees owed to sweep', value: feeOwed ? money(feeOwed.owed) : '—', icon: Banknote, href: '/portal-8k2m9x/fee-payouts' },
    ];

    const platform = [
      { label: 'Landlords', value: stats?.landlords?.total || 0, icon: Users, href: '/portal-8k2m9x/landlords' },
      { label: 'Tenants', value: stats?.tenants?.total || 0, icon: Users, href: '/portal-8k2m9x/tenants' },
      { label: 'Active tenancies', value: stats?.tenancies?.active || 0, icon: Home, href: '/portal-8k2m9x/tenancies' },
      { label: 'Properties', value: stats?.properties?.total || 0, icon: Building2, href: '/portal-8k2m9x/properties' },
      { label: 'Reports', value: stats?.reports?.total || 0, icon: FileText, href: '/portal-8k2m9x/reports' },
      { label: 'Total users', value: stats?.users?.total || 0, icon: Shield, href: '/portal-8k2m9x/users' },
    ];

    const quick = [
      { label: 'Landlords', href: '/portal-8k2m9x/landlords', icon: Users },
      { label: 'Subscriptions', href: '/portal-8k2m9x/subscriptions', icon: CreditCard },
      { label: 'Fee Payouts', href: '/portal-8k2m9x/fee-payouts', icon: Banknote },
      { label: 'Rent Wallets', href: '/portal-8k2m9x/wallets', icon: Wallet },
      { label: 'Tickets', href: '/portal-8k2m9x/support-tickets', icon: Headphones },
      { label: 'Payments', href: '/portal-8k2m9x/payments', icon: DollarSign },
      { label: 'Waitlist', href: '/portal-8k2m9x/waitlist', icon: Send },
      { label: 'Analytics', href: '/portal-8k2m9x/analytics', icon: Activity },
    ];

    return (
      <div className="space-y-6" data-testid="admin-overview">
        {/* Greeting */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{greeting}, {name}</h1>
            <p className="text-slate-500 text-sm">{dateStr} · {totalAttention > 0 ? `${totalAttention} item${totalAttention === 1 ? '' : 's'} need your attention` : 'Everything looks clear'}</p>
          </div>
        </div>

        {/* Needs attention */}
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Needs attention</h2>
          {totalAttention === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-3" data-testid="admin-attention-clear">
              <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle className="w-5 h-5" /></div>
              <div><p className="text-sm font-medium text-slate-900">All clear</p><p className="text-xs text-slate-500">No verifications, disputes, tickets or applications waiting.</p></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-attention">
              {actions.map(a => {
                const I = a.icon; const active = a.count > 0;
                return (
                  <a key={a.id} href={a.href} data-testid={`admin-attention-${a.id}`}
                    className={`group bg-white rounded-xl border p-4 transition-all hover:shadow-md hover:-translate-y-0.5 ${active ? 'border-slate-200' : 'border-slate-100'}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ring-1 ${active ? toneCls[a.tone] : 'text-slate-400 bg-slate-50 ring-slate-100'}`}><I className="w-4 h-4" /></div>
                    <p className={`mt-3 text-2xl font-bold tabular-nums ${active ? 'text-slate-900' : 'text-slate-300'}`}>{a.count}</p>
                    <p className="text-xs font-medium text-slate-700">{a.label}</p>
                    <p className="text-[11px] text-slate-400 truncate">{active ? a.hint : 'Nothing pending'}</p>
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Revenue & growth */}
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Revenue &amp; growth</h2>
          <div className="bg-white rounded-xl border border-slate-200 divide-y sm:divide-y-0 sm:divide-x divide-slate-100 grid grid-cols-2 sm:grid-cols-5" data-testid="admin-revenue">
            {revenue.map((r, i) => {
              const I = r.icon; const inner = (
                <>
                  <div className="flex items-center gap-1.5 text-slate-400 text-[11px]"><I className="w-3.5 h-3.5" />{r.label}</div>
                  <p className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{r.value}</p>
                </>
              );
              return r.href
                ? <a key={i} href={r.href} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`admin-revenue-${i}`}>{inner}</a>
                : <div key={i} className="p-4" data-testid={`admin-revenue-${i}`}>{inner}</div>;
            })}
          </div>
        </div>

        {/* Platform at a glance */}
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Platform at a glance</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-platform">
            {platform.map((p, i) => {
              const I = p.icon;
              return (
                <a key={i} href={p.href} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`admin-platform-${i}`}>
                  <I className="w-4 h-4 text-slate-400" />
                  <p className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{p.value.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{p.label}</p>
                </a>
              );
            })}
          </div>
        </div>

        {/* Pulse: activity trend + health */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="admin-trend">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-slate-900">Platform activity</h2>
              <span className="text-[11px] text-slate-400">Last 30 days</span>
            </div>
            <div className="flex flex-wrap gap-4 mb-3 text-[11px]">
              <span className="flex items-center gap-1.5 text-slate-500"><span className="w-2 h-2 rounded-full bg-teal-500" />+<b className="text-slate-800">{analyticsData?.users?.new_30d ?? 0}</b> users</span>
              <span className="flex items-center gap-1.5 text-slate-500"><span className="w-2 h-2 rounded-full bg-indigo-500" />+<b className="text-slate-800">{analyticsData?.reports?.new_30d ?? 0}</b> reports</span>
              <span className="text-slate-500">+<b className="text-slate-800">{analyticsData?.tenants?.new_30d ?? 0}</b> tenants</span>
            </div>
            <div className="h-56">
              {analyticsData?.time_series ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analyticsData.time_series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0d9488" stopOpacity={0.3} /><stop offset="100%" stopColor="#0d9488" stopOpacity={0} /></linearGradient>
                      <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} /><stop offset="100%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} interval={6} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    <Area type="monotone" dataKey="users" name="Users" stroke="#0d9488" strokeWidth={2} fill="url(#gU)" />
                    <Area type="monotone" dataKey="reports" name="Reports" stroke="#6366f1" strokeWidth={2} fill="url(#gR)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-xs text-slate-400">Loading activity…</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="admin-health">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Platform health</h2>
            <div className="space-y-4">
              {[
                { label: 'Property verification', val: analyticsData?.properties?.verification_rate, color: 'bg-teal-500' },
                { label: 'Occupancy', val: analyticsData?.tenancies?.occupancy_rate, color: 'bg-indigo-500' },
                { label: 'Dispute resolution', val: analyticsData?.disputes?.resolution_rate, color: 'bg-amber-500' },
                { label: 'Ticket resolution', val: analyticsData?.support?.resolution_rate, color: 'bg-emerald-500' },
              ].map((h, i) => (
                <div key={i} data-testid={`admin-health-${i}`}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{h.label}</span><span className="font-semibold text-slate-800 tabular-nums">{h.val ?? 0}%</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${h.color} transition-all`} style={{ width: `${Math.min(100, h.val || 0)}%` }} /></div>
                </div>
              ))}
            </div>
            <a href="/portal-8k2m9x/analytics" className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800" data-testid="admin-health-more">Full analytics <ChevronRight className="w-3.5 h-3.5" /></a>
          </div>
        </div>

        {/* Quick actions */}
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Jump to</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="admin-quick">
            {quick.map((q, i) => {
              const I = q.icon;
              return (
                <a key={i} href={q.href} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col items-center gap-2 text-center hover:shadow-md hover:-translate-y-0.5 hover:border-teal-200 transition-all" data-testid={`admin-quick-${i}`}>
                  <span className="w-9 h-9 rounded-lg bg-slate-50 text-slate-600 flex items-center justify-center group-hover:bg-teal-50"><I className="w-4 h-4" /></span>
                  <span className="text-xs font-medium text-slate-700">{q.label}</span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Generic List Render with actions
  const renderList = (config) => {
    const unsorted = data.items.filter(item => {
      if (!searchTerm) return true;
      const searchFields = config.searchFields || ['email', 'name'];
      return searchFields.some(f => item[f]?.toLowerCase?.()?.includes(searchTerm.toLowerCase()));
    });
    const sortVal = (item) => listSort.key === 'status'
      ? (config.getStatus?.(item) || '')
      : listSort.key === 'details'
        ? (config.getSubtitle?.(item) || item.email || '')
        : (item[config.nameField] || item.email || '');
    const filtered = [...unsorted].sort((a, b) => {
      const c = String(sortVal(a)).localeCompare(String(sortVal(b)), undefined, { numeric: true, sensitivity: 'base' });
      return listSort.dir === 'asc' ? c : -c;
    });
    const toggleListSort = (key) => setListSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    const SortBtn = ({ k, label }) => (
      <button type="button" onClick={() => toggleListSort(k)} className={`inline-flex items-center gap-1 uppercase tracking-widest hover:text-slate-700 ${listSort.key === k ? 'text-slate-700' : ''}`} data-testid={`admin-sort-${k}`}>
        {label}{(() => { const I = listSort.key === k ? (listSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown; return <I className={`w-3 h-3 ${listSort.key === k ? 'opacity-100' : 'opacity-40'}`} />; })()}
      </button>
    );
    const handleExport = () => exportCsv(`tenantra-admin-${config.title.toLowerCase().replace(/\s+/g, '-')}.csv`, filtered, [
      { label: 'Name', value: (i) => i[config.nameField] || i.email || '' },
      { label: 'Details', value: (i) => config.getSubtitle?.(i) || i.email || '' },
      { label: 'Status', value: (i) => config.getStatus?.(i) || '' },
      { label: 'ID', value: (i) => i.id || i.user_id || '' },
    ]);

    // Icon renderer based on type
    const renderIcon = (item) => {
      const iconMap = {
        home: <Home className="w-4 h-4 text-slate-500" />,
        file: <FileText className="w-4 h-4 text-slate-500" />,
        building: <Building2 className="w-4 h-4 text-slate-500" />,
        alert: <AlertTriangle className="w-4 h-4 text-red-500" />,
        user: null,
        users: <Users className="w-4 h-4 text-slate-500" />,
        scroll: <ScrollText className="w-4 h-4 text-slate-500" />,
      };
      if (config.iconType && iconMap[config.iconType]) return iconMap[config.iconType];
      return item[config.nameField]?.[0]?.toUpperCase() || '?';
    };

    return (
      <div>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-900">{config.title}</h1>
            <p className="text-slate-500 text-[11px]">{config.subtitle}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] text-slate-400 whitespace-nowrap">{filtered.length} of {data.items.length}</span>
            <ExportButton onClick={handleExport} disabled={filtered.length === 0} testId="admin-export-btn" />
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" data-testid="admin-list-search" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200">
          <div className="sticky top-12 z-10 flex items-center gap-3 px-3 py-1.5 bg-slate-50/90 backdrop-blur-sm border-b border-slate-200 rounded-t-lg text-[10px] font-semibold text-slate-400">
            <div className="w-8 flex-shrink-0" />
            <div className="flex-1 flex items-center gap-4"><SortBtn k="name" label="Name" /><SortBtn k="details" label="Details" /></div>
            {config.getStatus && <SortBtn k="status" label="Status" />}
          </div>
          <div className="divide-y divide-slate-100">
          {filtered.map((item) => (
            <div key={item.id || item.user_id} className="lens lens-row flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 px-3 py-2.5 hover:bg-slate-50" data-testid="admin-list-row">
              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-medium flex-shrink-0">
                {renderIcon(item)}
              </div>
              <div className="flex-1 min-w-0 basis-0">
                <p className="text-xs font-medium text-slate-900 truncate">{item[config.nameField] || item.email || 'N/A'}</p>
                <p className="text-[10px] text-slate-500 truncate">{config.getSubtitle?.(item) || item.email}</p>
              </div>
              {config.showScore && <span className="text-xs font-semibold text-slate-900">{item.score || '—'}</span>}
              {config.showMfa && item.mfa_enabled && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">MFA</span>}
              {config.getStatus && <StatusBadge status={config.getStatus(item)} />}
              {config.actions?.map((action, idx) => (
                <Button
                  key={idx}
                  size="sm"
                  variant={action.variant || 'outline'}
                  className={`h-7 px-2 text-[10px] ${action.className || ''}`}
                  onClick={() => action.onClick(item)}
                  disabled={actionLoading === item.id || actionLoading === item.user_id}
                >
                  {actionLoading === (item.id || item.user_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : action.label}
                </Button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No items found</div>}
          </div>
        </div>
      </div>
    );
  };

  // Render Subscriptions with assign plan support
  const renderSubscriptions = () => (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Subscription Management</h1>
          <p className="text-slate-500 text-[11px]">Assign or upgrade user subscriptions</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      {/* Assign Plan Modal */}
      {modal === 'assign_plan' && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg p-5 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-sm font-bold text-slate-900 mb-1">{modalData.plan ? 'Upgrade' : 'Assign'} Plan</h3>
            <p className="text-[11px] text-slate-500 mb-4">{modalData.email}</p>
            <div className="mb-4">
              <Label className="text-[11px] text-slate-600 mb-1 block">Select Plan</Label>
              <Select value={modalData.selectedPlan || ''} onValueChange={(v) => setModalData({ ...modalData, selectedPlan: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Choose plan" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="starter">Starter ($29/mo)</SelectItem>
                  <SelectItem value="professional">Professional ($79/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise ($199/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mb-4">
              <Label className="text-[11px] text-slate-600 mb-1 block">Billing Interval</Label>
              <Select value={modalData.selectedInterval || 'monthly'} onValueChange={(v) => setModalData({ ...modalData, selectedInterval: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="six_month">6-Month ({discountSettings.six_month_discount}% off)</SelectItem>
                  <SelectItem value="annual">Annual ({discountSettings.annual_discount}% off)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => setModal(null)}>Cancel</Button>
              <Button size="sm" className="flex-1 h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={() => handleAction('assign_plan', modalData.user_id, { plan: modalData.selectedPlan })} disabled={!modalData.selectedPlan || actionLoading}>
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {data.items.filter(s => s.email?.toLowerCase().includes(searchTerm.toLowerCase())).map((sub) => (
          <div key={sub.user_id} className="lens lens-row flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-medium">
              {sub.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-900 truncate">{sub.email}</p>
              <p className="text-[10px] text-slate-500">{sub.legal_name || sub.account_id || 'No account'}</p>
            </div>
            <div className="flex items-center gap-2">
              <PlanBadge plan={sub.plan} />
              {sub.status && <StatusBadge status={sub.status} />}
              {sub.billing_interval && <span className="text-[9px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">{sub.billing_interval}</span>}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[10px]"
              onClick={() => { setModal('assign_plan'); setModalData({ user_id: sub.user_id, email: sub.email, plan: sub.plan, selectedPlan: sub.plan || '' }); }}
            >
              {sub.plan ? 'Change' : 'Assign'}
            </Button>
          </div>
        ))}
        {data.items.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No users found</div>}
      </div>
    </div>
  );

  // Render Discounts with billing intervals
  const renderDiscounts = () => (
    <div>
      <div className="mb-4">
        <h1 className="text-lg font-bold text-slate-900">Discount Management</h1>
        <p className="text-slate-500 text-[11px]">Set discounts for plans and billing intervals — preview below shows what landlords will see</p>
      </div>

      <div className="max-w-xl space-y-4">
        {/* Base Plan Pricing */}
        <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-4" data-testid="base-pricing-card">
          <h3 className="text-xs font-semibold text-slate-900 mb-1">Base Monthly Pricing</h3>
          <p className="text-[10px] text-slate-500 mb-3">The list price each plan charges per month. Discounts below stack on top. Saved prices flow straight into Stripe Checkout for new subscriptions.</p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: 'base_starter', label: 'Starter' },
              { key: 'base_professional', label: 'Professional' },
              { key: 'base_enterprise', label: 'Enterprise' },
            ].map((p) => (
              <div key={p.key} className="bg-slate-50 rounded p-3">
                <Label className="text-xs text-slate-700">{p.label}</Label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-slate-500">$</span>
                  <Input type="number" min="0.5" max="9999" step="1" value={discountSettings[p.key]} onChange={(e) => setDiscountSettings({ ...discountSettings, [p.key]: parseFloat(e.target.value) || 0 })} className="w-20 h-7 text-xs text-center" data-testid={`price-input-${p.key}`} />
                  <span className="text-xs text-slate-500">/mo</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Free Trial */}
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4" data-testid="trial-card">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs font-semibold text-slate-900">Free Trial</Label>
              <p className="text-[10px] text-slate-500">New subscribers only · card collected up front · auto-converts when the trial ends</p>
            </div>
            <Switch checked={!!discountSettings.trial_enabled} onCheckedChange={(v) => setDiscountSettings({ ...discountSettings, trial_enabled: v })} data-testid="trial-toggle" />
          </div>
          {discountSettings.trial_enabled && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-emerald-200">
              <Label className="text-xs text-slate-700">Trial length</Label>
              <Input type="number" min="1" max="90" value={discountSettings.trial_days} onChange={(e) => setDiscountSettings({ ...discountSettings, trial_days: parseInt(e.target.value) || 0 })} className="w-16 h-7 text-xs text-center" data-testid="trial-days-input" />
              <span className="text-xs text-slate-500">days</span>
            </div>
          )}
        </div>

        {/* Global Discount */}
        <div className="bg-violet-50 rounded-lg border border-violet-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs font-semibold text-slate-900">Global Discount</Label>
              <p className="text-[10px] text-slate-500">Applies to all plans</p>
            </div>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="100" value={discountSettings.global_discount} onChange={(e) => setDiscountSettings({ ...discountSettings, global_discount: parseInt(e.target.value) || 0 })} className="w-16 h-7 text-xs text-center" />
              <span className="text-xs text-slate-500">%</span>
            </div>
          </div>
        </div>

        {/* Billing Interval Discounts */}
        <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-900 mb-3">Billing Interval Discounts</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div><Label className="text-xs text-slate-700">Monthly</Label><p className="text-[10px] text-slate-400">No discount (base)</p></div>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" max="100" value={discountSettings.monthly_discount} onChange={(e) => setDiscountSettings({ ...discountSettings, monthly_discount: parseInt(e.target.value) || 0 })} className="w-16 h-7 text-xs text-center" />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div><Label className="text-xs text-slate-700">6-Month</Label><p className="text-[10px] text-slate-400">Semi-annual billing</p></div>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" max="100" value={discountSettings.six_month_discount} onChange={(e) => setDiscountSettings({ ...discountSettings, six_month_discount: parseInt(e.target.value) || 0 })} className="w-16 h-7 text-xs text-center" />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div><Label className="text-xs text-slate-700">Annual</Label><p className="text-[10px] text-slate-400">Yearly billing</p></div>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" max="100" value={discountSettings.annual_discount} onChange={(e) => setDiscountSettings({ ...discountSettings, annual_discount: parseInt(e.target.value) || 0 })} className="w-16 h-7 text-xs text-center" />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Discounts */}
        <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-900 mb-3">Plan-Specific Discounts</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'starter_discount', label: 'Starter', price: `$${discountSettings.base_starter}/mo list` },
              { key: 'professional_discount', label: 'Professional', price: `$${discountSettings.base_professional}/mo list` },
              { key: 'enterprise_discount', label: 'Enterprise', price: `$${discountSettings.base_enterprise}/mo list` },
            ].map((plan) => (
              <div key={plan.key} className="bg-slate-50 rounded p-3">
                <div className="flex items-center justify-between">
                  <div><Label className="text-xs text-slate-700">{plan.label}</Label><p className="text-[10px] text-slate-400">{plan.price}</p></div>
                  <div className="flex items-center gap-1">
                    <Input type="number" min="0" max="100" value={discountSettings[plan.key]} onChange={(e) => setDiscountSettings({ ...discountSettings, [plan.key]: parseInt(e.target.value) || 0 })} className="w-14 h-7 text-xs text-center" />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DiscountPreview settings={discountSettings} />

        <Button className="w-full h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSaveDiscounts} disabled={actionLoading === 'discounts'} data-testid="save-discounts-btn">
          {actionLoading === 'discounts' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Save Pricing
        </Button>
        <p className="text-[10px] text-slate-500">Base prices and discounts apply immediately to the landlord plan selector and flow into Stripe Checkout (discounts as a coupon line; the free trial as a trial period). Discounts stack (interval + plan + global, capped at 90%).</p>
      </div>
    </div>
  );

  // Render Support Tickets
  const renderSupportTickets = () => (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Support Tickets</h1>
          <p className="text-slate-500 text-[11px]">Manage customer support requests</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg p-5 max-w-lg w-full mx-4 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono text-slate-500">{selectedTicket.ticket_number}</span>
                  <StatusBadge status={selectedTicket.status} />
                </div>
                <h3 className="text-sm font-bold text-slate-900">{selectedTicket.subject}</h3>
                <p className="text-[10px] text-slate-500">{selectedTicket.user_email}</p>
              </div>
              <button onClick={() => { setSelectedTicket(null); setTicketComment(''); setTicketStatus(''); }} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-3 bg-slate-50 rounded text-xs text-slate-700 mb-4">{selectedTicket.description}</div>
            <div className="space-y-3 border-t border-slate-200 pt-3">
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Update Status</Label>
                <Select value={ticketStatus} onValueChange={setTicketStatus}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Add Comment</Label>
                <Textarea value={ticketComment} onChange={(e) => setTicketComment(e.target.value)} placeholder="Staff response..." className="text-xs min-h-[80px]" />
              </div>
              <Button className="w-full h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={handleUpdateTicket} disabled={(!ticketStatus && !ticketComment) || actionLoading}>
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Update Ticket
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {data.items.filter(t => t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) || t.user_email?.toLowerCase().includes(searchTerm.toLowerCase())).map((ticket) => (
          <div key={ticket.id} className="lens lens-row flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedTicket(ticket)}>
            <div className={`w-8 h-8 rounded flex items-center justify-center ${ticket.priority === 'urgent' ? 'bg-red-100' : ticket.priority === 'high' ? 'bg-amber-100' : 'bg-slate-100'}`}>
              <Headphones className={`w-4 h-4 ${ticket.priority === 'urgent' ? 'text-red-600' : ticket.priority === 'high' ? 'text-amber-600' : 'text-slate-500'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-[9px] font-mono text-slate-400">{ticket.ticket_number}</span>
                {ticket.priority === 'urgent' && <span className="px-1 py-0.5 bg-red-100 text-red-700 text-[8px] font-semibold rounded">URGENT</span>}
                {ticket.priority === 'high' && <span className="px-1 py-0.5 bg-amber-100 text-amber-700 text-[8px] font-semibold rounded">HIGH</span>}
              </div>
              <p className="text-xs font-medium text-slate-900 truncate">{ticket.subject}</p>
              <p className="text-[10px] text-slate-500">{ticket.user_email}</p>
            </div>
            <StatusBadge status={ticket.status} />
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
        ))}
        {data.items.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No tickets found</div>}
      </div>
    </div>
  );

  // ============================================================================
  // NEWSLETTER
  // ============================================================================
  const EMAIL_TYPES = [
    { value: 'product_update', label: 'Product Update' },
    { value: 'company_news', label: 'Company News' },
    { value: 'feature_announcement', label: 'Feature Announcement' },
    { value: 'industry_insight', label: 'Industry Insight' },
    { value: 'maintenance', label: 'Service Notice' },
  ];

  const handleSendNewsletter = async () => {
    if (!newsletterForm.subject.trim() || !newsletterForm.body_html.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSendingNewsletter(true);
    try {
      const res = await api.post('/admin/newsletter/send', newsletterForm);
      toast.success(res.data.message);
      setNewsletterForm({ subject: '', body_html: '', email_type: 'product_update' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingNewsletter(false);
    }
  };

  const renderNewsletter = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Newsletter</h2>
        <p className="text-slate-500 text-[11px]">Send emails to {subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Compose */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Compose Email</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Email Type</Label>
            <Select value={newsletterForm.email_type} onValueChange={(v) => setNewsletterForm(prev => ({ ...prev, email_type: v }))}>
              <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {EMAIL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Subject</Label>
            <Input
              value={newsletterForm.subject}
              onChange={(e) => setNewsletterForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="e.g. New feature: Auto-complete addresses"
              className="h-8 text-xs bg-white"
              data-testid="newsletter-subject"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Body (HTML supported)</Label>
          <Textarea
            value={newsletterForm.body_html}
            onChange={(e) => setNewsletterForm(prev => ({ ...prev, body_html: e.target.value }))}
            placeholder="Write your message here. HTML tags like <b>, <a>, <br>, <ul>, <li> are supported.&#10;&#10;The email will automatically include a professional header with your branding, a personalized greeting (Hi [first name]), your signature, and an unsubscribe link."
            className="min-h-[140px] text-xs bg-white"
            data-testid="newsletter-body"
          />
        </div>

        <div className="flex items-center justify-between pt-1">
          <p className="text-[10px] text-slate-400">
            Auto-includes: Header, greeting, signature, unsubscribe link
          </p>
          <Button
            onClick={handleSendNewsletter}
            disabled={sendingNewsletter || subscribers.length === 0}
            className="h-8 px-4 text-xs bg-teal-600 hover:bg-teal-700 text-white border-0"
            data-testid="send-newsletter-btn"
          >
            {sendingNewsletter ? (
              <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Sending...</span>
            ) : (
              <span className="flex items-center gap-1.5"><Send className="w-3 h-3" /> Send to {subscribers.length}</span>
            )}
          </Button>
        </div>
      </div>

      {/* Subscribers */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Subscribers ({subscribers.length})</h3>
        </div>
        {subscribers.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {subscribers.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-2" data-testid={`subscriber-${s.id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-slate-800 truncate block">{s.name || 'Unknown'}</span>
                  <span className="text-[10px] text-slate-400 truncate block">{s.email}</span>
                </div>
                <span className="text-[10px] text-slate-400">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 text-center py-6">No subscribers yet</p>
        )}
      </div>

      {/* Campaign History */}
      {campaigns.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Sent Campaigns</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {campaigns.map((c) => (
              <div key={c.id} className="px-4 py-2.5 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-800 truncate">{c.subject}</p>
                  <p className="text-[10px] text-slate-400">{EMAIL_TYPES.find(t => t.value === c.email_type)?.label || c.email_type} &middot; {new Date(c.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <span className="text-xs text-emerald-600">{c.sent_count} sent</span>
                  {c.failed_count > 0 && <span className="text-xs text-red-400 ml-2">{c.failed_count} failed</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );



  // Render Payments with billing stats and adjustments
  const renderPayments = () => {
    const formatAmount = (amount) => {
      if (typeof amount === 'number') {
        // Check if already in dollars (> 1000 likely cents)
        return amount > 1000 ? `$${(amount / 100).toFixed(2)}` : `$${amount.toFixed(2)}`;
      }
      return '$0.00';
    };

    return (
      <div>
        <div className="mb-4">
          <h1 className="text-lg font-bold text-slate-900">Payments & Billing</h1>
          <p className="text-slate-500 text-[11px]">Platform-wide payment history and billing management</p>
        </div>

        {/* Adjustment Modal */}
        {modal === 'adjust_payment' && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-lg p-5 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-sm font-bold text-slate-900 mb-3">Bill Adjustment</h3>
              <p className="text-[11px] text-slate-500 mb-4">Payment: {modalData.id}</p>
              <div className="space-y-3 mb-4">
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Type</Label>
                  <Select value={modalData.adjustType || 'credit'} onValueChange={(v) => setModalData({ ...modalData, adjustType: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="refund">Refund</SelectItem>
                      <SelectItem value="adjustment">Adjustment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Amount ($)</Label>
                  <Input type="number" step="0.01" value={modalData.adjustAmount || ''} onChange={(e) => setModalData({ ...modalData, adjustAmount: e.target.value })} className="h-8 text-xs" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Reason</Label>
                  <Input value={modalData.adjustReason || ''} onChange={(e) => setModalData({ ...modalData, adjustReason: e.target.value })} className="h-8 text-xs" placeholder="Reason for adjustment" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => setModal(null)}>Cancel</Button>
                <Button size="sm" className="flex-1 h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={() => handleAction('adjust_payment', modalData.id, { type: modalData.adjustType, amount: parseFloat(modalData.adjustAmount), reason: modalData.adjustReason })} disabled={!modalData.adjustAmount || !modalData.adjustReason || actionLoading}>
                  {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Billing Stats */}
        {billingStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total Revenue</p>
              <p className="text-xl font-bold text-slate-900">{formatAmount(billingStats.total_revenue || 0)}</p>
            </div>
            <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">MRR</p>
              <p className="text-xl font-bold text-emerald-600">${billingStats.mrr || 0}</p>
            </div>
            <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Active Subs</p>
              <p className="text-xl font-bold text-slate-900">{billingStats.active_subscriptions?.total || 0}</p>
            </div>
            <div className="lens lens-card bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Last 30d</p>
              <p className="text-xl font-bold text-slate-900">{billingStats.recent_payments_30d || 0}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
          {data.items.map((payment) => (
            <div key={payment.id} className="lens lens-row flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${payment.payment_type === 'refund' ? 'bg-red-50' : 'bg-emerald-50'}`}>
                <DollarSign className={`w-4 h-4 ${payment.payment_type === 'refund' ? 'text-red-600' : 'text-emerald-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium text-slate-900">{formatAmount(payment.amount)}</p>
                  {payment.account_id && <span className="text-[9px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{payment.account_id}</span>}
                </div>
                <p className="text-[10px] text-slate-500">{payment.user_email} - {payment.payment_type || payment.type || 'payment'}</p>
              </div>
              <StatusBadge status={payment.status} />
              <span className="text-[10px] text-slate-400">{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}</span>
              <Button variant="outline" size="sm" className="h-6 px-2 text-[9px]" onClick={() => { setModal('adjust_payment'); setModalData({ id: payment.id, adjustType: 'credit' }); }}>
                Adjust
              </Button>
            </div>
          ))}
          {data.items.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No payments found</div>}
        </div>
      </div>
    );
  };

  // Render Email Templates (Editable)
  const renderEmailTemplates = () => {
    const handleEditTemplate = (template) => {
      setEditingTemplate(template.id);
      setTemplateForm({
        subject: template.subject,
        doc_title: template.doc_title,
        doc_subtitle: template.doc_subtitle,
        intro: template.intro
      });
      setPreviewHtml('');
    };

    const handleSaveTemplate = async () => {
      if (!editingTemplate) return;
      setActionLoading(editingTemplate);
      try {
        await api.put(`/email-templates/${editingTemplate}`, templateForm);
        toast.success('Template saved');
        setEditingTemplate(null);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save');
      } finally {
        setActionLoading(null);
      }
    };

    const handleResetTemplate = async (templateId) => {
      setActionLoading(templateId);
      try {
        await api.delete(`/email-templates/${templateId}`);
        toast.success('Template reset to default');
        fetchData();
      } catch (err) {
        toast.error('Failed to reset');
      } finally {
        setActionLoading(null);
      }
    };

    const handlePreviewTemplate = async () => {
      if (!editingTemplate) return;
      setActionLoading('preview');
      try {
        const res = await api.post(`/email-templates/${editingTemplate}/preview`, templateForm);
        setPreviewHtml(res.data.preview_html);
      } catch (err) {
        toast.error('Failed to generate preview');
      } finally {
        setActionLoading(null);
      }
    };

    if (editingTemplate) {
      const template = emailTemplates.find(t => t.id === editingTemplate);
      return (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-slate-900">Edit Template</h1>
              <p className="text-slate-500 text-[11px]">{template?.name}</p>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setEditingTemplate(null)}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Email Subject</Label>
                <Input value={templateForm.subject || ''} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Document Title</Label>
                  <Input value={templateForm.doc_title || ''} onChange={(e) => setTemplateForm({ ...templateForm, doc_title: e.target.value })} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Document Subtitle</Label>
                  <Input value={templateForm.doc_subtitle || ''} onChange={(e) => setTemplateForm({ ...templateForm, doc_subtitle: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Introduction Text</Label>
                <Textarea value={templateForm.intro || ''} onChange={(e) => setTemplateForm({ ...templateForm, intro: e.target.value })} className="text-xs min-h-[120px]" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSaveTemplate} disabled={actionLoading === editingTemplate}>
                  {actionLoading === editingTemplate ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handlePreviewTemplate} disabled={actionLoading === 'preview'}>
                  {actionLoading === 'preview' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                  Preview
                </Button>
              </div>
            </div>

            {previewHtml && (
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-[10px] font-semibold text-slate-600">Preview</span>
                </div>
                <iframe srcDoc={previewHtml} className="w-full h-[400px]" title="Email Preview" />
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="mb-4">
          <h1 className="text-lg font-bold text-slate-900">Email Templates</h1>
          <p className="text-slate-500 text-[11px]">Manage all platform email templates</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
          {emailTemplates.map((template) => (
            <div key={template.id} className="lens lens-row flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
              <div className="w-8 h-8 rounded bg-violet-50 flex items-center justify-center">
                <Mail className="w-4 h-4 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium text-slate-900">{template.name}</p>
                  {template.is_custom && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Custom</span>}
                </div>
                <p className="text-[10px] text-slate-500 truncate">{template.description}</p>
              </div>
              <div className="flex items-center gap-2">
                {template.is_custom && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[9px] text-slate-500" onClick={() => handleResetTemplate(template.id)} disabled={actionLoading === template.id}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-6 px-2 text-[9px]" onClick={() => handleEditTemplate(template)}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
            </div>
          ))}
          {emailTemplates.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No email templates found</div>}
        </div>
      </div>
    );
  };


  // Render Disputes Admin - full trail view
  const renderDisputesAdmin = () => {
    const statusDot = { open: 'bg-amber-500', landlord_responded: 'bg-blue-500', under_review: 'bg-blue-500', resolved: 'bg-emerald-500' };
    const statusLabel = { open: 'Awaiting Response', landlord_responded: 'Landlord Responded', under_review: 'Under Review', resolved: 'Resolved' };
    const reasonLabels = { not_my_tenancy: 'Not My Tenancy', incorrect_dates: 'Incorrect Dates', incorrect_amount: 'Incorrect Amount', damage_not_caused: 'Damage Not Caused', payment_was_made: 'Payment Was Made', other: 'Other' };
    const decisionLabels = { remove_entry: 'Remove Entry', keep_entry: 'Keep Entry', modify_entry: 'Modify Entry' };

    const handleResolve = async (disputeId) => {
      if (!resolveNotes.trim()) { alert('Please add admin notes before resolving.'); return; }
      await handleAction('resolve_dispute', disputeId, { decision: resolveDecision, admin_notes: resolveNotes });
      setExpandedDispute(null);
      setResolveNotes('');
    };

    return (
      <div>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h1 className="text-sm font-semibold text-slate-900">Disputes</h1>
            <p className="text-[11px] text-slate-400">Review and resolve tenant disputes</p>
          </div>
          <div className="flex items-center gap-2">
            {['all', 'open', 'landlord_responded', 'resolved'].map(s => (
              <button key={s} onClick={() => { setSearchTerm(''); fetchData(`/admin/disputes/all${s === 'all' ? '' : `?status=${s}`}`); }}
                className={`text-[11px] px-2.5 py-1 rounded-md border ${!searchTerm && (s === 'all') ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                {s === 'all' ? 'All' : statusLabel[s]}
              </button>
            ))}
          </div>
        </div>

        {(data.items || data || []).length === 0 ? (
          <div className="text-center py-10 border border-slate-200 rounded-md">
            <p className="text-xs text-slate-500">No disputes found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {(data.items || data || []).map(d => (
              <div key={d.id} className="border border-slate-200 rounded-md">
                {/* Summary row */}
                <button onClick={() => setExpandedDispute(expandedDispute === d.id ? null : d.id)}
                  className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 text-left" data-testid={`admin-dispute-${d.id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot[d.status] || 'bg-slate-400'}`} />
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-4 gap-1 sm:gap-3 items-center">
                    <div>
                      <p className="text-xs font-medium text-slate-800 truncate">{d.tenant_name}</p>
                      <p className="text-[10px] text-slate-400 sm:hidden">{d.property_address || 'Unknown'}</p>
                    </div>
                    <p className="hidden sm:block text-xs text-slate-600 truncate">{d.property_address || 'Unknown'}</p>
                    <p className="hidden sm:block text-xs text-slate-500">{reasonLabels[d.reason] || d.reason}</p>
                    <div className="hidden sm:flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">{new Date(d.created_at).toLocaleDateString()}</span>
                      <span className={`text-[10px] font-medium ${d.status === 'resolved' ? 'text-emerald-600' : d.status === 'open' ? 'text-amber-600' : 'text-blue-600'}`}>
                        {statusLabel[d.status] || d.status}
                      </span>
                    </div>
                  </div>
                </button>

                {/* Expanded detail */}
                {expandedDispute === d.id && (
                  <div className="border-t border-slate-100 px-3 py-3 bg-slate-50/50 space-y-3">
                    {/* Tenant's Dispute */}
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Tenant's Dispute</p>
                      <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div><p className="text-[10px] text-slate-400">Tenant</p><p className="text-xs text-slate-800">{d.tenant_name}</p></div>
                          <div><p className="text-[10px] text-slate-400">Email</p><p className="text-xs text-slate-800">{d.tenant_email}</p></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><p className="text-[10px] text-slate-400">Reason</p><p className="text-xs text-slate-800">{reasonLabels[d.reason] || d.reason}</p></div>
                          <div><p className="text-[10px] text-slate-400">Filed</p><p className="text-xs text-slate-800">{new Date(d.created_at).toLocaleDateString()}</p></div>
                        </div>
                        <div><p className="text-[10px] text-slate-400">Explanation</p><p className="text-xs text-slate-700 whitespace-pre-wrap">{d.explanation || 'No explanation provided'}</p></div>
                      </div>
                    </div>

                    {/* Landlord's Response */}
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Landlord's Response</p>
                      <div className="bg-white border border-slate-200 rounded-md p-3">
                        <div className="mb-2"><p className="text-[10px] text-slate-400">Landlord</p><p className="text-xs text-slate-800">{d.landlord_name || 'Unknown'}</p></div>
                        {d.landlord_response ? (
                          <div><p className="text-[10px] text-slate-400">Response</p><p className="text-xs text-slate-700 whitespace-pre-wrap">{d.landlord_response}</p></div>
                        ) : (
                          <p className="text-xs text-slate-400 italic">No response yet</p>
                        )}
                      </div>
                    </div>

                    {/* Report Details */}
                    {d.report_period && (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Disputed Report</p>
                        <div className="bg-white border border-slate-200 rounded-md p-3 grid grid-cols-3 gap-2">
                          <div><p className="text-[10px] text-slate-400">Period</p><p className="text-xs text-slate-800">{d.report_period}</p></div>
                          <div><p className="text-[10px] text-slate-400">Payment</p><p className="text-xs text-slate-800 capitalize">{d.report_payment_status?.replace('_',' ') || 'N/A'}</p></div>
                          <div><p className="text-[10px] text-slate-400">Lease</p><p className="text-xs text-slate-800 capitalize">{d.report_lease_compliance?.replace('_',' ') || 'N/A'}</p></div>
                        </div>
                      </div>
                    )}

                    {/* Resolution */}
                    {d.status === 'resolved' ? (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Resolution</p>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-1.5">
                          <div><p className="text-[10px] text-slate-400">Decision</p><p className="text-xs font-medium text-emerald-700">{decisionLabels[d.admin_decision] || d.admin_decision}</p></div>
                          {d.admin_notes && <div><p className="text-[10px] text-slate-400">Admin Notes</p><p className="text-xs text-slate-700">{d.admin_notes}</p></div>}
                          {d.resolved_at && <p className="text-[10px] text-slate-400">Resolved {new Date(d.resolved_at).toLocaleDateString()}</p>}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Resolve This Dispute</p>
                        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2.5">
                          <div>
                            <label className="text-[11px] text-slate-500 mb-1 block">Decision</label>
                            <select value={resolveDecision} onChange={(e) => setResolveDecision(e.target.value)}
                              className="h-8 w-full rounded-md bg-white border border-slate-200 text-xs text-slate-700 px-2 focus:outline-none focus:ring-1 focus:ring-teal-500">
                              <option value="keep_entry">Keep Entry (No change)</option>
                              <option value="remove_entry">Remove Entry (Favor tenant)</option>
                              <option value="modify_entry">Modify Entry</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] text-slate-500 mb-1 block">Admin Notes</label>
                            <textarea value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)}
                              placeholder="Explain your decision..."
                              className="w-full h-20 rounded-md border border-slate-200 text-xs text-slate-700 p-2 resize-none focus:outline-none focus:ring-1 focus:ring-teal-500" />
                          </div>
                          <button onClick={() => handleResolve(d.id)}
                            className="bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-medium h-8 px-4 rounded-md">
                            Resolve Dispute
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // Render Properties with Edit functionality
  const renderProperties = () => (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Properties</h1>
          <p className="text-slate-500 text-[11px]">Manage all properties</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      {/* Edit Property Modal */}
      {editingProperty && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg p-5 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">Edit Property</h3>
              <button onClick={() => { setEditingProperty(null); setPropertyForm({}); }} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Property Name</Label>
                <Input value={propertyForm.name || ''} onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })} className="h-8 text-xs" placeholder="Property name" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Street Address</Label>
                <Input value={propertyForm.street_address || ''} onChange={(e) => setPropertyForm({ ...propertyForm, street_address: e.target.value })} className="h-8 text-xs" placeholder="Street address" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">City</Label>
                  <Input value={propertyForm.city || ''} onChange={(e) => setPropertyForm({ ...propertyForm, city: e.target.value })} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Province</Label>
                  <Input value={propertyForm.province || ''} onChange={(e) => setPropertyForm({ ...propertyForm, province: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Postal Code</Label>
                  <Input value={propertyForm.postal_code || ''} onChange={(e) => setPropertyForm({ ...propertyForm, postal_code: e.target.value })} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Status</Label>
                  <Select value={propertyForm.verification_status || ''} onValueChange={(v) => setPropertyForm({ ...propertyForm, verification_status: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white mt-2" onClick={handleUpdateProperty} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {data.items.filter(p => p.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.city?.toLowerCase().includes(searchTerm.toLowerCase())).map((prop) => (
          <div key={prop.id} className="lens lens-row flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Home className="w-4 h-4 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-900 truncate">{prop.display_name || prop.street_address || 'Unknown'}</p>
              <p className="text-[10px] text-slate-500 truncate">{prop.city || ''}, {prop.province || ''} - {prop.landlord_email || ''}</p>
            </div>
            <StatusBadge status={prop.verification_status} />
            <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={() => { setEditingProperty(prop); setPropertyForm({ name: prop.name || '', street_address: prop.street_address || prop.address?.street || '', city: prop.city || prop.address?.city || '', province: prop.province || prop.address?.province || '', postal_code: prop.postal_code || prop.address?.postal_code || '', verification_status: prop.verification_status || '' }); }}>
              <Edit className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleAction('delete_property', prop.id)} disabled={actionLoading === prop.id}>
              {actionLoading === prop.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Delete'}
            </Button>
          </div>
        ))}
        {data.items.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No properties found</div>}
      </div>
    </div>
  );

  // Render Tenants with Edit functionality
  const renderTenants = () => (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Tenants</h1>
          <p className="text-slate-500 text-[11px]">Manage all tenants</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      {/* Edit Tenant Modal */}
      {editingTenant && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg p-5 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">Edit Tenant</h3>
              <button onClick={() => { setEditingTenant(null); setTenantForm({}); }} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Full Name</Label>
                <Input value={tenantForm.full_name || ''} onChange={(e) => setTenantForm({ ...tenantForm, full_name: e.target.value })} className="h-8 text-xs" placeholder="Full name" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Email</Label>
                <Input value={tenantForm.email || ''} onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })} className="h-8 text-xs" placeholder="Email address" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Phone</Label>
                <Input value={tenantForm.phone || ''} onChange={(e) => setTenantForm({ ...tenantForm, phone: e.target.value })} className="h-8 text-xs" placeholder="Phone number" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Score</Label>
                  <Input type="number" value={tenantForm.score || ''} onChange={(e) => setTenantForm({ ...tenantForm, score: parseInt(e.target.value) || 0 })} className="h-8 text-xs" placeholder="0-100" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Rating</Label>
                  <Select value={tenantForm.rating || ''} onValueChange={(v) => setTenantForm({ ...tenantForm, rating: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Rating" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white mt-2" onClick={handleUpdateTenant} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {data.items.filter(t => t.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || t.email?.toLowerCase().includes(searchTerm.toLowerCase())).map((tenant) => (
          <div key={tenant.id} className="lens lens-row flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0 text-xs font-medium text-slate-600">
              {tenant.full_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-900 truncate">{tenant.full_name || 'Unknown'}</p>
              <p className="text-[10px] text-slate-500 truncate">{tenant.email}</p>
            </div>
            {tenant.score && <span className="text-xs font-semibold text-slate-900">{tenant.score}</span>}
            <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={() => { setEditingTenant(tenant); setTenantForm({ full_name: tenant.full_name || '', email: tenant.email || '', phone: tenant.phone || '', score: tenant.score || '', rating: tenant.rating || '' }); }}>
              <Edit className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleAction('delete_tenant', tenant.id)} disabled={actionLoading === tenant.id}>
              {actionLoading === tenant.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Delete'}
            </Button>
          </div>
        ))}
        {data.items.length === 0 && <div className="p-6 text-center text-slate-500 text-xs">No tenants found</div>}
      </div>
    </div>
  );

  // Render Analytics with professional charts
  const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];
  
  const renderAnalytics = () => {
    if (!analyticsData) return <TableSkeleton rows={6} spans={[3, 3, 3, 3]} />;
    
    const { overview, users, reports, disputes, properties, tenants, tenancies, support, subscriptions, payments, time_series, distribution } = analyticsData;
    
    // Prepare horizontal bar data for status comparisons
    const statusData = [
      { name: 'Properties', verified: properties?.verified || 0, pending: properties?.pending || 0, total: properties?.total || 0 },
      { name: 'Disputes', resolved: disputes?.resolved || 0, pending: disputes?.pending || 0, total: disputes?.total || 0 },
      { name: 'Tickets', resolved: support?.resolved || 0, open: support?.open || 0, total: support?.total_tickets || 0 },
    ];

    // Revenue breakdown data
    const revenueData = [
      { name: 'Total Revenue', value: overview?.total_revenue || 0 },
      { name: 'MRR', value: overview?.mrr || 0 },
    ];
    
    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Analytics Dashboard</h1>
            <p className="text-slate-500 text-[11px]">Real-time platform performance metrics</p>
          </div>
          <div className="text-[10px] text-slate-400">Last updated: {new Date().toLocaleString()}</div>
        </div>

        {/* Top KPI Row - Clean numbers only */}
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
          {[
            { label: 'Users', value: overview?.total_users || 0 },
            { label: 'Landlords', value: overview?.total_landlords || 0 },
            { label: 'Tenants', value: overview?.total_tenants || 0 },
            { label: 'Properties', value: overview?.total_properties || 0 },
            { label: 'Tenancies', value: overview?.active_tenancies || 0 },
            { label: 'Reports', value: overview?.total_reports || 0 },
            { label: 'Revenue', value: `$${overview?.total_revenue || 0}` },
            { label: 'MRR', value: `$${overview?.mrr || 0}` },
          ].map((item, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-slate-900">{item.value}</p>
              <p className="text-[9px] text-slate-500 uppercase tracking-wide">{item.label}</p>
            </div>
          ))}
        </div>

        {/* Main Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Line Chart - User & Tenant Growth Trend */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">30-Day Activity Trend</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={time_series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#94a3b8" interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                <Line type="monotone" dataKey="users" name="Users" stroke="#6366f1" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="tenants" name="Tenants" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="properties" name="Properties" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Vertical Bar Chart - Entity Totals */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Platform Entities</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={[
                { name: 'Users', count: overview?.total_users || 0, fill: '#6366f1' },
                { name: 'Landlords', count: overview?.total_landlords || 0, fill: '#8b5cf6' },
                { name: 'Tenants', count: overview?.total_tenants || 0, fill: '#10b981' },
                { name: 'Properties', count: overview?.total_properties || 0, fill: '#f59e0b' },
                { name: 'Reports', count: overview?.total_reports || 0, fill: '#06b6d4' },
              ]} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} stroke="#94a3b8" width={70} />
                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {[
                    { name: 'Users', fill: '#6366f1' },
                    { name: 'Landlords', fill: '#8b5cf6' },
                    { name: 'Tenants', fill: '#10b981' },
                    { name: 'Properties', fill: '#f59e0b' },
                    { name: 'Reports', fill: '#06b6d4' },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Breakdown Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Stacked Bar - Status Comparison */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Status Breakdown</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { name: 'Properties', Verified: properties?.verified || 0, Pending: properties?.pending || 0 },
                { name: 'Disputes', Resolved: disputes?.resolved || 0, Pending: disputes?.pending || 0 },
                { name: 'Tickets', Resolved: support?.resolved || 0, Open: support?.open || 0 },
              ]} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} stroke="#94a3b8" width={70} />
                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="Verified" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Resolved" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Pending" stackId="a" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                <Bar dataKey="Open" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-4 mt-2 text-[9px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded"></span> Verified/Resolved</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded"></span> Pending</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded"></span> Open</span>
            </div>
          </div>

          {/* Area Chart - Daily Report Activity */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Report Submissions (30 Days)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={time_series || []}>
                <defs>
                  <linearGradient id="reportGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#94a3b8" interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                <Area type="monotone" dataKey="reports" name="Reports" stroke="#06b6d4" fill="url(#reportGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Rate Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'User Growth Rate', value: users?.growth_rate || 0, suffix: '%', sub: `+${users?.new_30d || 0} new (30d)`, color: 'text-indigo-600' },
            { label: 'Property Verification', value: properties?.verification_rate || 0, suffix: '%', sub: `${properties?.verified || 0} of ${properties?.total || 0}`, color: 'text-emerald-600' },
            { label: 'Dispute Resolution', value: disputes?.resolution_rate || 0, suffix: '%', sub: `${disputes?.resolved || 0} resolved`, color: 'text-amber-600' },
            { label: 'Ticket Resolution', value: support?.resolution_rate || 0, suffix: '%', sub: `${support?.resolved || 0} of ${support?.total_tickets || 0}`, color: 'text-cyan-600' },
          ].map((metric, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{metric.label}</p>
              <p className={`text-2xl font-bold ${metric.color}`}>{metric.value}{metric.suffix}</p>
              <p className="text-[9px] text-slate-400 mt-1">{metric.sub}</p>
            </div>
          ))}
        </div>

        {/* Bottom Summary Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Subscriptions */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Subscriptions</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-xs"><span className="text-slate-500">Total</span><span className="font-semibold">{subscriptions?.total || 0}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Active</span><span className="font-semibold text-emerald-600">{subscriptions?.active || 0}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Conversion Rate</span><span className="font-semibold">{subscriptions?.conversion_rate || 0}%</span></div>
            </div>
          </div>

          {/* Payments */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Payments</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-xs"><span className="text-slate-500">Total Transactions</span><span className="font-semibold">{payments?.total || 0}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Successful</span><span className="font-semibold text-emerald-600">{payments?.successful || 0}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Success Rate</span><span className="font-semibold">{payments?.success_rate || 0}%</span></div>
            </div>
          </div>

          {/* Tenancies */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Tenancies</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-xs"><span className="text-slate-500">Total</span><span className="font-semibold">{tenancies?.total || 0}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Active</span><span className="font-semibold text-emerald-600">{tenancies?.active || 0}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Occupancy Rate</span><span className="font-semibold">{tenancies?.occupancy_rate || 0}%</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main content renderer
  // ========== Client Documents ==========
  const [clientDocs, setClientDocs] = useState({ clients: [], total_clients: 0 });
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientDocsLoading, setClientDocsLoading] = useState(false);
  const [clientDocsFetched, setClientDocsFetched] = useState(false);

  const fetchClientDocs = async () => {
    try {
      setClientDocsLoading(true);
      const resp = await api.get('/admin/client-documents');
      setClientDocs(resp.data);
      setClientDocsFetched(true);
    } catch (err) {
      toast.error('Failed to load client documents');
    } finally {
      setClientDocsLoading(false);
    }
  };

  useEffect(() => {
    if (currentPath.includes('client-documents') && !clientDocsFetched && !clientDocsLoading) {
      fetchClientDocs();
    }
  }, [currentPath]);

  const downloadDoc = async (docId, filename) => {
    try {
      const resp = await api.get(`/admin/client-documents/download/${docId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderClientDocuments = () => {
    if (clientDocsLoading && clientDocs.clients.length === 0) {
      return <TableSkeleton rows={5} spans={[6, 3, 3]} />;
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Client Documents</h1>
            <p className="text-[11px] text-slate-500">{clientDocs.total_clients} client folder{clientDocs.total_clients !== 1 ? 's' : ''}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { setClientDocsFetched(false); fetchClientDocs(); }} className="h-8 text-xs" data-testid="refresh-client-docs">
            <RotateCcw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {clientDocs.clients.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No documents uploaded yet</p>
            <p className="text-xs text-slate-400 mt-1">Documents will appear here when clients upload property ownership files</p>
          </div>
        ) : (
          <div className="space-y-2">
            {clientDocs.clients.map((client) => (
              <div key={client.folder_name} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                {/* Client folder header */}
                <button
                  onClick={() => setExpandedClient(expandedClient === client.folder_name ? null : client.folder_name)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`client-folder-${client.folder_name}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="w-4 h-4 text-violet-600" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{client.client_name}</p>
                      <p className="text-[10px] text-slate-400">Acct: {client.account_number} &middot; {client.total_files} file{client.total_files !== 1 ? 's' : ''} &middot; {formatFileSize(client.total_size)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 hidden sm:block">
                      Last upload: {new Date(client.latest_upload).toLocaleDateString()}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedClient === client.folder_name ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Expanded file list */}
                {expandedClient === client.folder_name && (
                  <div className="border-t border-slate-100 bg-slate-50/50">
                    <div className="px-4 py-2">
                      <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_1fr_auto_auto] gap-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 pb-1.5 border-b border-slate-200">
                        <span>File</span>
                        <span className="hidden sm:block">Property</span>
                        <span>Date</span>
                        <span>Action</span>
                      </div>
                      {client.documents.map((doc) => (
                        <div key={doc.id} className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-center py-2 px-2 border-b border-slate-100 last:border-0">
                          <div className="min-w-0">
                            <p className="text-xs text-slate-800 truncate">{doc.original_filename}</p>
                            <p className="text-[10px] text-slate-400">{formatFileSize(doc.file_size)}</p>
                          </div>
                          <p className="text-[11px] text-slate-500 truncate hidden sm:block">{doc.property_address}</p>
                          <p className="text-[10px] text-slate-400 whitespace-nowrap">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadDoc(doc.id, doc.original_filename)}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-teal-600"
                            data-testid={`download-doc-${doc.id}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return <TableSkeleton rows={8} spans={[5, 3, 2, 2]} />;
    
    if (currentPath === '/portal-8k2m9x/dashboard' || currentPath === '/portal-8k2m9x') return renderDashboard();
    if (currentPath.includes('equifax-reporting')) return <EquifaxReportingPanel />;
    if (currentPath.includes('payment-fees')) return <PaymentFeesPanel />;
    if (currentPath.includes('fee-payouts')) return <FeePayoutsPanel />;
    if (currentPath.includes('fee-revenue')) return <FeeRevenuePanel />;
    if (currentPath.includes('/wallets')) return <WalletsPanel />;
    if (currentPath.includes('admin-team')) return <AdminStaffPanel />;
    if (currentPath.includes('waitlist')) return <WaitlistPanel />;
    if (currentPath.includes('contacts')) return <ContactsPanel />;
    if (currentPath.includes('analytics')) return renderAnalytics();
    if (currentPath.includes('subscriptions')) return renderSubscriptions();
    if (currentPath.includes('discounts')) return renderDiscounts();
    if (currentPath.includes('support-tickets')) return renderSupportTickets();
    if (currentPath.includes('careers')) return <CareersPanel />;
    if (currentPath.includes('ownership-review')) return <PropertyReviewPanel />;
    if (currentPath.includes('promo-codes')) return <PromoCodesPanel />;
    if (currentPath.includes('payments')) return renderPayments();
    if (currentPath.includes('email-templates')) return renderEmailTemplates();
    if (currentPath.includes('newsletter')) return renderNewsletter();
    if (currentPath.includes('client-documents')) {
      return renderClientDocuments();
    }
    if (currentPath.includes('properties') && !currentPath.includes('dashboard')) return renderProperties();
    if (currentPath.includes('tenants') && !currentPath.includes('dashboard')) return renderTenants();
    
    // Generic list views
    if (currentPath.includes('landlords')) return renderList({
      title: 'Landlords', subtitle: 'All landlords — approve or reject pending verifications', nameField: 'legal_name', searchFields: ['legal_name', 'email'],
      getSubtitle: (i) => i.email, getStatus: (i) => i.id_verification_status, iconType: 'user',
      actions: [
        { label: 'Approve', variant: 'default', className: 'bg-emerald-600 hover:bg-emerald-700 text-white', onClick: (i) => i.id_verification_status !== 'verified' && handleAction('approve_landlord', i.id) },
        { label: 'Reject', onClick: (i) => i.id_verification_status !== 'verified' && handleAction('reject_landlord', i.id) },
      ]
    });
    if (currentPath.includes('reports')) return renderList({
      title: 'Reports', subtitle: 'Manage all rent reports', nameField: 'tenant_name', searchFields: ['tenant_name', 'tenant_email', 'property_name'],
      iconType: 'file', getSubtitle: (i) => `${i.property_name || 'Unknown Property'} - ${i.payment?.status || 'N/A'}`,
      getStatus: (i) => i.is_disputed ? 'disputed' : (i.payment?.status === 'on_time' ? 'active' : 'pending'),
      actions: [{ label: 'Delete', className: 'text-red-600 hover:text-red-700 hover:bg-red-50', onClick: (i) => handleAction('delete_report', i.id) }]
    });
    if (currentPath.includes('tenancies')) return renderList({
      title: 'Tenancies', subtitle: 'Manage all tenancies', nameField: 'property_name', searchFields: ['property_name', 'tenant_name'],
      iconType: 'building', getSubtitle: (i) => `Tenant: ${i.tenant_name}${i.consent_withdrawn ? ' · Consent withdrawn' : ''}`,
      getStatus: (i) => i.consent_withdrawn ? 'consent_withdrawn' : i.status,
      actions: [
        { label: 'Withdraw consent', className: 'text-amber-700 hover:text-amber-800 hover:bg-amber-50', onClick: (i) => !i.consent_withdrawn && window.confirm(`Record a consent withdrawal from ${i.tenant_name}? This tenancy will be excluded from all bureau reporting.`) && handleAction('withdraw_consent', i.id) },
        { label: 'Delete', className: 'text-red-600 hover:text-red-700 hover:bg-red-50', onClick: (i) => handleAction('delete_tenancy', i.id) }
      ]
    });
    if (currentPath.includes('disputes')) return renderDisputesAdmin();
    if (currentPath.includes('users')) return renderList({
      title: 'Users', subtitle: 'Manage user accounts', nameField: 'email', searchFields: ['email'],
      getSubtitle: (i) => i.role, getStatus: (i) => i.status, showMfa: true, iconType: 'user',
      actions: [
        { label: 'Disable MFA', onClick: (i) => i.mfa_enabled && handleAction('disable_mfa', i.id) },
        { label: 'Delete', className: 'text-red-600 hover:text-red-700 hover:bg-red-50', onClick: (i) => i.role !== 'admin' && handleAction('delete_user', i.id) },
      ]
    });
    if (currentPath.includes('teams')) return renderList({
      title: 'Teams', subtitle: 'Manage all teams', nameField: 'name', searchFields: ['name', 'owner_email'],
      getSubtitle: (i) => `Owner: ${i.owner_email} - ${i.member_count || 0} members`, iconType: 'users',
      actions: [{ label: 'Delete', className: 'text-red-600 hover:text-red-700 hover:bg-red-50', onClick: (i) => handleAction('delete_team', i.id) }]
    });
    if (currentPath.includes('audit')) return renderList({
      title: 'Audit Log', subtitle: 'View platform activity', nameField: 'action', searchFields: ['action', 'admin_email'],
      getSubtitle: (i) => `${i.admin_email || i.user_email || 'System'} - ${formatDate(i.timestamp || i.created_at, true)}`,
      getStatus: (i) => i.action?.includes('approve') ? 'approved' : i.action?.includes('reject') ? 'rejected' : 'pending', iconType: 'scroll',
    });
    if (currentPath.includes('settings')) return <AdminSettings user={user} onRefresh={fetchData} />;
    
    return renderDashboard();
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} counts={counts} />
      <AdminCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <main className="min-h-screen lg:ml-[220px]">
        <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/80" data-testid="admin-topbar">
          <div className="px-4 h-12 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 hover:bg-slate-100 rounded text-slate-600" aria-label="Open menu" data-testid="admin-sidebar-toggle"><Menu className="w-5 h-5" /></button>
              <h1 className="text-sm font-semibold text-slate-900">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-1">
              <CommandTrigger onClick={() => setPaletteOpen(true)} />
              <button className="p-1.5 hover:bg-slate-100 rounded text-slate-600" onClick={fetchData}><RotateCcw className="w-4 h-4" /></button>
              <button className="p-1.5 hover:bg-slate-100 rounded text-slate-600 relative">
                <Bell className="w-4 h-4" />
                {(counts.openDisputes > 0 || counts.openTickets > 0) && <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />}
              </button>
            </div>
          </div>
        </div>
        <div className="p-4">{renderContent()}</div>
      </main>
    </div>
  );
}
